import { motion } from "framer-motion";
import {
  Fingerprint,
  ClipboardList,
  Compass,
  LineChart,
} from "lucide-react";

const benefits = [
  {
    id: "curriculum",
    title: "Curriculum Customisation",
    tag: "Powered by ProBodh DNA",
    body:
      "ProBodh DNA customises the curriculum, teaching flow and grading logic to the DNA of your school — your board, your rubric, your department, your teachers. What used to live only in the heads of your best educators now lives inside the platform.",
    icon: Fingerprint,
    color: "bg-mint",
  },
  {
    id: "assessment",
    title: "Auto Question Paper & Assessment",
    tag: "Powered by ProBodh Quest",
    body:
      "ProBodh Quest turns a scanned past paper into a searchable, taggable question bank in minutes. AI generates fresh question papers in your school's style and grades submissions — online or hand-written — to your rubric.",
    icon: ClipboardList,
    color: "bg-yellow",
  },
  {
    id: "how-to-study",
    title: "How to Study",
    tag: "Powered by ProBodh Focus",
    body:
      "ProBodh Focus teaches students how to study — not rote learning, not exam-centric cramming. The Feynman flow, active recall and metacognitive reflection are built into every chapter, with an AI coach that refuses to give away the answer.",
    icon: Compass,
    color: "bg-pink",
  },
  {
    id: "reporting",
    title: "Progress Report & Access Control",
    tag: "Administration + Parents",
    body:
      "Administration and parents get real visibility into every child's progress, gaps and every teacher's efficiency. All communication — remarks, homework, updates, meetings — stays on a single platform, with role-based access for the school, the family and the classroom.",
    icon: LineChart,
    color: "bg-lavender",
  },
];

function BenefitCard({ b, i }) {
  const Icon = b.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`relative rounded-2xl md:rounded-[28px] ${b.color} p-6 md:p-8 lg:p-10 flex flex-col overflow-hidden group hover:-translate-y-1 transition-transform duration-500 card-shadow min-h-[320px] md:min-h-[380px]`}
      data-testid={`benefit-card-${b.id}`}
    >
      <div className="flex items-start justify-between mb-6 md:mb-8">
        <div className="text-[11px] md:text-[12px] uppercase tracking-[0.18em] font-semibold text-ink/70 max-w-[70%]">
          {b.tag}
        </div>
        <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-white-card/80 flex items-center justify-center text-ink shrink-0">
          <Icon size={20} strokeWidth={1.7} />
        </div>
      </div>

      <h3 className="font-display text-2xl md:text-3xl lg:text-[34px] leading-tight text-ink max-w-[16ch]">
        {b.title}
      </h3>

      <p className="mt-4 md:mt-5 text-[14px] md:text-[15px] leading-relaxed text-ink/85 max-w-[52ch] flex-1">
        {b.body}
      </p>
    </motion.article>
  );
}

export default function Benefits() {
  return (
    <section
      id="benefits"
      className="relative bg-off py-16 md:py-24"
      data-testid="benefits-section"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="text-[12px] md:text-[13px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
            The workflows
          </div>
          <h2 className="mt-3 md:mt-4 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-ink tracking-tight">
            One platform for all your{" "}
            <span className="font-italic-serif text-ink">
              teaching &amp; learning workflows.
            </span>
          </h2>
          <p className="mt-4 md:mt-5 text-[15px] md:text-[16px] leading-relaxed text-ink-soft max-w-[54ch]">
            From setting the curriculum to teaching a child how to study, ProBodh
            replaces the four different tools most institutions cobble together.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {benefits.map((b, i) => (
            <BenefitCard key={b.id} b={b} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
